import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Checkbox } from '../../../../components/UI';
import { MktScope } from '../../../../services/marketing/marketing';
import { launchLimitText, normaliseScopes, ScopeInfo, scopeInfo, SCOPES } from '../ai_keys';

// #region LaunchWarning
/** Shown whenever launch is ticked: what it lets the AI do, and what still protects people. */
export const LaunchWarning: React.FunctionComponent<{ limit?: number | null; onChangeLimit?: () => void; limitLink?: React.ReactNode }> = ({ limit, onChangeLimit, limitLink }): React.ReactElement => (
    <div role="alert" className="flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-900 dark:border-red-800 dark:bg-red-500/10 dark:text-red-200">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 space-y-1">
            <p className="font-semibold">This AI can email people without anyone reviewing it first</p>
            <p>
                It can send campaigns and turn flows on by itself, for at most the launch limit of people each time
                {limit != null ? <> (now {launchLimitText(limit)})</> : null}. Consent, unsubscribes, the frequency cap, warm-up and the bounce and complaint breaker still apply.
            </p>
            {onChangeLimit ? (
                <button type="button" onClick={onChangeLimit} className="font-medium underline underline-offset-2">Change the launch limit</button>
            ) : limitLink ?? null}
        </div>
    </div>
);

interface Props {
    value: MktScope[];
    onChange: (scopes: MktScope[]) => void;
    /** Which scopes to list (default: all). Unknown ones are listed by name. */
    shown?: string[];
    /** Scopes that may be ticked; the rest are listed but locked. Default: all. */
    grantable?: string[];
    idPrefix: string;
}

// #region ScopePicker
/** The scope checkboxes with their plain descriptions. Read is always on; manage brings draft with it. */
export const ScopePicker: React.FunctionComponent<Props> = ({ value, onChange, shown, grantable, idPrefix }): React.ReactElement => {
    const list: ScopeInfo[] = shown
        ? shown.map((s) => scopeInfo(s) ?? { scope: s as MktScope, label: s, chip: s, help: 'Wallmates does not know this one yet.' })
        : SCOPES;
    const included = new Set(value.flatMap((s) => scopeInfo(s)?.includes ?? []));

    const toggle = (s: MktScope, on: boolean): void =>
        onChange(normaliseScopes(on ? [...value, s] : value.filter((x) => x !== s)));

    return (
        <ul className="space-y-3">
            {list.map((s) => {
                const locked = !!grantable && !grantable.includes(s.scope);
                const forced = s.always || (included.has(s.scope) && value.includes(s.scope));
                const id = `${idPrefix}-${s.scope.replace(':', '-')}`;
                return (
                    <li key={s.scope} className="flex items-start gap-3">
                        <Checkbox id={id} color={s.tone === 'red' ? 'red' : 'emerald'} className="mt-0.5" disabled={locked || forced}
                            checked={!locked && (s.always || value.includes(s.scope))} onChange={(on: boolean) => toggle(s.scope, on)} />
                        <label htmlFor={id} className={`min-w-0 text-sm ${locked ? 'opacity-60' : ''}`}>
                            <span className="block font-medium text-zinc-900 dark:text-zinc-100">
                                {s.label}
                                {s.tone === 'red' ? <span className="ml-2 rounded bg-red-500/15 px-1.5 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300">Sends without review</span> : null}
                            </span>
                            <span className="block text-xs text-zinc-500 dark:text-zinc-400">{s.help}</span>
                            {locked ? <span className="block text-xs text-zinc-500 dark:text-zinc-400">You can&apos;t give this one here.</span>
                                : forced && !s.always ? <span className="block text-xs text-zinc-500 dark:text-zinc-400">Included with manage.</span> : null}
                        </label>
                    </li>
                );
            })}
        </ul>
    );
};
